// React

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import { Card, Fade, Icon, Menu, MenuItem, Modal, Select, Tooltip } from "@mui/material";

import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import ModalDelete from "components/Modals/Delete";
import CustomPagination from "components/Pagination";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import proveedoresData from "layouts/materiaPrima/data/proveedoresData";
import { getUproColor } from "utils/colors";
import ModalAddProveedor from "./ModalAdd";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "70%", xxl: "60%" },
  overflowY: "auto",
};

const GET_PROVEEDORES = gql`
  query getProveedores($search: String, $page: Int, $order: Order) {
    paginationInfo {
      pages
      total
    }
    proveedores(
      search: $search
      pagination: { page: $page, limit: 10 }
      order: $order
    ) {
      id
      tipoProveedor
      razonSocial
      nombre
      apellido
      tipoDocumento
      documento
      tipoResponsable
      localidad {
        id
        nombre
        provincia {
          id
          nombre
        }
      }
    }
  }
`;

const DELETE_PROVEEDOR = gql`
  mutation deleteProveedor($id: ID!) {
    deleteProveedor(id: $id) {
      id
    }
  }
`;

export default function ModalProveedores({ open, handleClose }) {
  const [proveedor, setProveedor] = useState();
  const [rows, setRows] = useState([]);
  const [openMenu, setOpenMenu] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState();
  const { columns, getRows } = proveedoresData;
  const { handleSnackbar } = useContext(MessageManager);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "nombre",
    orden: "ASC",
  });
  const [openModalEdit, setOpenModalEdit] = useState(false);

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setPage(1);
      setSearch(event.target.value);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const [getProveedores, { loading, data: proveedores }] = useLazyQuery(GET_PROVEEDORES, {
    variables: {
      search: search || null,
      page: page,
      order: { field: orderBy.campo, order: orderBy.orden },
    },
    fetchPolicy: "no-cache",
    cachePolicy: "no-cache",
  });
  const [deleteProveedor] = useMutation(DELETE_PROVEEDOR);

  const handleDelete = (id) => {
    deleteProveedor({
      variables: {
        id,
      },
    })
      .then(() => {
        getProveedores();
        handleSnackbar("Proveedor eliminado correctamente", "success");
      })
      .catch(() => {
        handleSnackbar("Error al eliminar el proveedor", "error");
      });
  };

  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedItem(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        onClick={() => {
          setOpenModalEdit(true);
          setProveedor({
            tipoProveedor: selectedItem.tipoProveedor,
            nombre: selectedItem.nombre,
            apellido: selectedItem.apellido,
            tipoDocumento: selectedItem.tipoDocumento,
            documento: selectedItem.documento,
            tipoResponsable: selectedItem.tipoResponsable,
            razonSocial: selectedItem.razonSocial,
            provincia: selectedItem.localidad?.provincia?.id,
            localidad: selectedItem.localidad?.id,
            id: selectedItem.id,
            idsCategorias: selectedItem.categorias?.map((c) => c.id) || [],
            user: selectedItem.user,
          });
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Editar</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproYellow"),
              }}
            >
              edit
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      <MenuItem
        onClick={() => {
          setOpenModalDelete(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Eliminar</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproPink"),
              }}
            >
              delete
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
    </Menu>
  );
  useEffect(() => {
    setProveedor();
    getProveedores();
    setSearch("");
  }, [open]);

  useEffect(() => {
    if (proveedores && proveedores?.proveedores) {
      setRows(getRows(proveedores.proveedores, handleOpenMenu));
      setPagesInfo(proveedores?.paginationInfo);
    }
  }, [proveedores]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" p={3}>
              <SoftBox>
                <SoftTypography variant="h6">Proveedores</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="flex-end">
                <SoftBox mr={2}>
                  <SoftInput
                    placeholder="Escribe aquí..."
                    icon={{ component: "search", direction: "left" }}
                    onChange={handleSearch}
                  />
                </SoftBox>
                <SoftBox mr={2}>
                  <Tooltip title="Agregar nuevo proveedor" placement="top">
                    <SoftButton color="primary" circular onClick={() => setOpenModalEdit(true)}>
                      <Icon>add</Icon>
                      &nbsp;Agregar
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
                <SoftBox display="flex" alignItems="center">
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox
              sx={{
                maxHeight: "50vh",
                overflow: "auto",
              }}
            >
              {loading ? (
                <Loading />
              ) : proveedores?.proveedores?.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={proveedores?.proveedores?.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
            {renderMenu}
          </SoftBox>
          <ModalAddProveedor
            open={openModalEdit}
            handleClose={() => {
              setOpenModalEdit(false);
              setProveedor();
            }}
            data={proveedor}
            refetch={
              () => getProveedores()
            }
          />
          <ModalDelete
            open={openModalDelete}
            handleClose={() => {
              setOpenModalDelete(false);
              setSelectedItem();
            }}
            nombre={`el proveedor ${
              selectedItem?.razonSocial || `${selectedItem?.nombre} ${selectedItem?.apellido}`
            }`}
            functionDelete={() => {
              handleDelete(selectedItem?.id);
              setSelectedItem();
            }}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalProveedores.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};
